import fetch from "cross-fetch";
import { Helper } from "../helpers/helper";

let idmsBaseUrl = "https://iimport-ca.cloud.csiinc.com/task";

/**
 * Executes a task on the IDMS (iMIS data import) service and, when the initial call returns a polling (202) status, repeatedly polls the returned location URL until the task completes.
 * @param executionId The ID of the IDMS task/execution to run.
 * @param payload The request body to send with the initial execution call. Defaults to an empty object.
 * @param isPolling Whether to use the polling endpoint/behavior and wait for task completion; when `false`, uses the non-polling execute endpoint and returns immediately. Defaults to `true`.
 * @returns A promise resolving to the parsed JSON response if the final response has a 200 status, otherwise the raw response text.
 */
async function executeIdmsTask(executionId: string, payload = {}, isPolling = true) {
    const url: string = isPolling ? `${idmsBaseUrl}/executepolling/${executionId}` : `${idmsBaseUrl}/execute/${executionId}`;
    let executionResponse = await fetch(url, { method: "POST", body: JSON.stringify(payload), headers: { "Content-Type": "application/json" } });
    let executionResponseData = await executionResponse.text();
    if (!executionResponse.ok) {
        throw new Error(`Failed to execute IDMS task. Status: ${executionResponse.status}, Message: ${JSON.stringify(executionResponseData)}`);
    }

    let isPollingStatusCode = () => {
        return executionResponse.status == 202;
    };
    let delayTask = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    if (isPolling && isPollingStatusCode()) {
        // get the location url from the response headers
        const pollingUrl = executionResponse.headers.get("Location");
        const pollingRetryAfter = Number.parseInt(executionResponse.headers.get("Retry-After") || "30");
        if (Helper.isStringNullOrEmpty(pollingUrl)) {
            throw new Error("Polling URL not found in the response headers.");
        }

        do {
            await delayTask(pollingRetryAfter * 1000);
            executionResponse = await fetch(pollingUrl, { method: "GET", headers: { "Content-Type": "application/json" } });
            executionResponseData = await executionResponse.text();
            if (!executionResponse.ok) {
                throw new Error(`Failed to poll IDMS task. Status: ${executionResponse.status}, Message: ${JSON.stringify(executionResponseData)}`);
            }
        } while (isPollingStatusCode());
    }

    return executionResponse.status == 200 ? JSON.parse(executionResponseData) : executionResponseData;
}

export {
    idmsBaseUrl,
    executeIdmsTask,
};
